/**
 * Tastaturwege — Liste von Definitionen, erweiterbar ohne verstreute Handler.
 * v3: Umschalt statt Strg/Alt.
 */

export type TastaturBefehl = {
  id: string;
  /** Anzeige, z. B. „Umschalt+C“ */
  label: string;
  /** true = Shift nötig */
  umschalt: boolean;
  /**
   * true = Strg beziehungsweise ⌘ nötig.
   *
   * Die Vorgabe des Hauses ist Umschalt, nicht Strg — deshalb ist dieses Feld
   * die Ausnahme und nicht die Regel. Nötig geworden ist es durch den Entwurf:
   * Er schreibt für die Suche ausdrücklich `⌘K`, und das ist die Tastenfolge,
   * die jeder aus anderen Werkzeugen mitbringt. Ohne dieses Feld müsste die
   * Suche ihren eigenen Handler bekommen — genau das, was diese Datei
   * verhindern soll.
   */
  strg?: boolean;
  /** Kleinbuchstabe oder Symbol */
  taste: string;
  beschreibung: string;
  /**
   * Auch in Eingabefeldern gültig. Sonst greift die Suche nicht, während der
   * Zeiger in einem Textfeld steht — und dort sucht man besonders oft.
   */
  inFeldern?: boolean;
};

/** Paket-10-Befehle; spätere Pakete hängen sich hier an. */
export const TASTATUR_BEFEHLE: readonly TastaturBefehl[] = [
  {
    id: "aufgabe_neu",
    label: "Umschalt+C",
    umschalt: true,
    taste: "c",
    beschreibung: "Neue Aufgabe erfassen",
  },
  {
    id: "suche",
    label: "⌘K",
    umschalt: false,
    strg: true,
    taste: "k",
    beschreibung: "Suche öffnen",
    inFeldern: true,
  },
  {
    id: "hilfe",
    label: "?",
    umschalt: false,
    taste: "?",
    beschreibung: "Kürzelübersicht ein-/ausblenden",
  },
];

export function treffeBefehl(
  ereignis: KeyboardEvent,
  befehle: readonly TastaturBefehl[] = TASTATUR_BEFEHLE,
): TastaturBefehl | null {
  if (ereignis.altKey) return null;
  const ziel = ereignis.target as HTMLElement | null;
  const tag = ziel?.tagName;
  const inFeld =
    tag === "INPUT" || tag === "TEXTAREA" || Boolean(ziel?.isContentEditable);

  const strg = ereignis.ctrlKey || ereignis.metaKey;
  const taste = ereignis.key.length === 1 ? ereignis.key.toLowerCase() : ereignis.key;
  for (const b of befehle) {
    // Strg/⌘ muss auf beiden Seiten übereinstimmen: Ein Befehl ohne `strg`
    // darf nicht mit gedrückter Taste feuern, sonst löst ⌘C „Neue Aufgabe" aus
    // und das Kopieren wäre kaputt.
    if (Boolean(b.strg) !== strg) continue;
    if (inFeld && !b.inFeldern) continue;

    // „?“ oft als Shift+/ — hier vor dem Umschalt-Filter abfangen
    if (
      b.taste === "?" &&
      (ereignis.key === "?" ||
        taste === "?" ||
        (ereignis.shiftKey && (taste === "/" || ereignis.key === "/")))
    ) {
      return b;
    }
    if (b.umschalt !== ereignis.shiftKey) continue;
    if (b.taste === taste || b.taste === ereignis.key.toLowerCase()) return b;
  }
  return null;
}
